import {
    faPencil,
    faPlus,
    faTrash,
    faChevronLeft,
    faChevronRight,
    faEye,
    faEyeSlash,
    faCalendarAlt,
    faPaperPlane,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import { PermissionWrapper } from "../../helpers/permission.js";
import './AnalystViews.css';

const AnalystViews = () => {
    const [analystViews, setAnalystViews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [delId, setDelId] = useState(0);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedView, setSelectedView] = useState(null);
    const navigate = useNavigate();

    // 🔎 Filters
    const [dateFilter, setDateFilter] = useState("");
    const [marketStatusFilter, setMarketStatusFilter] = useState("");
    const [isActiveFilter, setIsActiveFilter] = useState("all");

    // 📑 Pagination states
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 10;

    useEffect(() => {
        fetchAnalystViews();
    }, [page, dateFilter, marketStatusFilter, isActiveFilter]);

    const fetchAnalystViews = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("authToken");

            const response = await axios.get(`${API_URL}analyst-views`, {
                params: {
                    page: page,
                    limit: limit,
                    isActive: isActiveFilter === "all" ? undefined : isActiveFilter === "active",
                    date: dateFilter || undefined,
                    marketStatus: marketStatusFilter || undefined,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                    accept: "*/*",
                },
            });

            if (response.data?.success && response.data?.data?.data) {
                const { data, total, page: currentPage, limit: currentLimit } = response.data.data;

                setAnalystViews(data || []);
                setTotal(total || 0);
                setPageCount(Math.ceil(total / currentLimit));
                setPage(currentPage);
            } else {
                setAnalystViews([]);
                setTotal(0);
            }

            setLoading(false);
        } catch (err) {
            console.error("Error fetching analyst views:", err);
            setError("Failed to fetch analyst views. Please try again later.");
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pageCount) {
            setPage(newPage);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [dateFilter, marketStatusFilter, isActiveFilter]);

    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);

    const openDetailsModal = (view) => {
        setSelectedView(view);
        setShowDetailsModal(true);
    };

    const handleDelete = async (id) => {
        setDelId(id);
        Swal.fire({
            title: "Are you sure?",
            text: "You will not be able to recover this Analyst View!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonClass: "btn-danger",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "No, cancel",
            reverseButtons: true,
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("authToken");

                    const response = await axios.delete(`${API_URL}analyst-views/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                            Accept: "application/json",
                        },
                    });

                    if (response.status === 200 || response.data.success) {
                        Swal.fire("Deleted!", "Analyst view has been deleted.", "success");
                        fetchAnalystViews();
                    } else {
                        Swal.fire(
                            "Error",
                            response.data.message || "Failed to delete the analyst view.",
                            "error"
                        );
                    }
                } catch (err) {
                    console.error("Error deleting analyst view:", err);
                    Swal.fire(
                        "Error",
                        err.response?.data?.message || "Failed to delete the analyst view.",
                        "error"
                    );
                }
            }
        });
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const token = localStorage.getItem("authToken");

            const response = await axios.patch(
                `${API_URL}analyst-views/${id}/status`,
                { isActive: !currentStatus },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.success) {
                showToast.success(`Analyst view ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
                fetchAnalystViews();
            }
        } catch (err) {
            console.error("Error toggling status:", err);
            showToast.error("Failed to update status");
        }
    };

    // Push Notification functionality
    const handlePushNotification = async () => {
        Swal.fire({
            title: "Push Notification",
            text: "Are you sure you want to send push notification for all analyst views?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, send push!",
            cancelButtonText: "Cancel",
            confirmButtonClass: "btn-success",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("authToken");

                    // Show loading
                    Swal.fire({
                        title: 'Sending Push Notification...',
                        text: 'Please wait while we send the notification',
                        allowOutsideClick: false,
                        didOpen: () => {
                            Swal.showLoading();
                        }
                    });

                    // Call the push API endpoint
                    const response = await axios.post(
                        `${API_URL}analyst-views/push`,
                        {}, // Empty body as per your curl example
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "accept": "*/*"
                            },
                        }
                    );

                    Swal.close();

                    if (response.data?.success || response.status === 200) {
                        showToast.success("Push notification sent successfully!");

                        Swal.fire({
                            icon: 'success',
                            title: 'Success!',
                            text: 'Push notification has been sent to all users.',
                            confirmButtonText: 'OK'
                        });
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: 'Push Failed',
                            text: response.data?.message || 'Failed to send push notification',
                        });
                    }
                } catch (err) {
                    console.error("Error sending push notification:", err);

                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: err.response?.data?.message || 'Failed to send push notification. Please try again.',
                    });
                }
            }
        });
    };

    const handleRetry = () => {
        setError(null);
        fetchAnalystViews();
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const renderHighlights = (highlights) => {
        if (!highlights || !Array.isArray(highlights)) return "N/A";

        return (
            <ul className="mb-0 ps-3" style={{ fontSize: "0.85rem" }}>
                {highlights.slice(0, 2).map((highlight, index) => (
                    <li key={index} className="text-truncate" title={highlight}>
                        {highlight}
                    </li>
                ))}
                {highlights.length > 2 && (
                    <li className="text-muted">+{highlights.length - 2} more</li>
                )}
            </ul>
        );
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text) return "N/A";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };

    const renderAllHighlights = (highlights) => {
        if (!highlights || !Array.isArray(highlights) || highlights.length === 0) {
            return <span className="text-muted">No highlights available</span>;
        }

        return (
            <ul className="mb-0 ps-3">
                {highlights.map((highlight, index) => (
                    <li key={index}>{highlight}</li>
                ))}
            </ul>
        );
    };

    return (
        <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
            <div id="content" className="">
                <div className="row pb-2">
                    <div className="col-md-6">
                        <ul className="breadcrumb">
                            <li className="breadcrumb-item">
                                <a href="/Dashboard">
                                    <i className="ti-home"></i> Home
                                </a>
                            </li>
                            <li className="breadcrumb-item active">Analyst Views</li>
                        </ul>
                    </div>
                    <div className="col-md-6 text-end d-flex justify-content-end align-items-center gap-2">
                        {/* Push Notification Button */}
                        <button
                            className="btn btn-success btn-sm d-flex align-items-center"
                            onClick={handlePushNotification}
                            title="Send push notification to all users"
                        >
                            <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                            Push Notification
                        </button>

                        <PermissionWrapper module="analystView" action="add">
                            <button
                                className="btn btn-default btn-sm"
                                onClick={() => navigate(`/research/analystView/add`)}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Add Analyst View
                            </button>
                        </PermissionWrapper>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger">
                        {error}
                        <button
                            className="btn btn-sm btn-outline-danger ml-2"
                            onClick={handleRetry}
                        >
                            Retry
                        </button>
                    </div>
                )}

                <div className="panel panel-default with-rounded-corner with-shadow p-10">
                    <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                        <h4 className="m-0">Manage Analyst Views</h4>
                        <div>
                            {total > 0 && (
                                <div className="d-inline-block">
                                    <span style={{ fontSize: "0.8rem" }} className="me-2">
                                        {start} to {end} of {total}
                                    </span>
                                    <button
                                        className="btn btn-default btn-sm"
                                        title="Previous"
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 1}
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} />
                                    </button>
                                    &nbsp;
                                    <button
                                        className="btn btn-default btn-sm"
                                        title="Next"
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === pageCount}
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {loading || analystViews.length > 0 ? (
                        <div className="table-responsive" style={{ maxHeight: "calc(100vh - 250px)", overflow: "auto" }}>
                            <table className="table table-condensed table-bordered bg-white m-0">
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: "center" }}>#</th>
                                        <th>Date</th>
                                        <th>Unique ID</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                    {/* 🔎 Search Filters Row */}
                                    <tr>
                                        <th></th>
                                        <th>
                                            <input
                                                type="date"
                                                className="form-control input-sm"
                                                value={dateFilter}
                                                onChange={(e) => setDateFilter(e.target.value)}
                                                style={{ fontSize: "12px" }}
                                            />
                                        </th>
                                        <th></th>
                                        <th>
                                            <select
                                                className="input-sm"
                                                value={isActiveFilter}
                                                onChange={(e) => setIsActiveFilter(e.target.value)}
                                                style={{ fontSize: "12px" }}
                                            >
                                                <option value="all">All</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </th>
                                        <th>
                                            <button
                                                className="btn btn-default btn-sm"
                                                onClick={() => {
                                                    setDateFilter("");
                                                    setMarketStatusFilter("");
                                                    setIsActiveFilter("all");
                                                }}
                                                style={{ fontSize: "12px", padding: "2px 6px" }}
                                            >
                                                Clear
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        Array.from({ length: limit }).map((_, i) => (
                                            <tr key={i} className="skeleton-row">
                                                {Array.from({ length: 5 }).map((_, j) => (
                                                    <td key={j}><div className="skeleton-box" /></td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : analystViews.length > 0 ? (
                                        analystViews.map((view, index) => (
                                            <tr key={view.id}>
                                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                    {(page - 1) * limit + index + 1}
                                                </td>
                                                <td style={{ verticalAlign: "middle" }}>
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon
                                                            icon={faCalendarAlt}
                                                            className="me-2 text-muted"
                                                            style={{ fontSize: "0.8rem" }}
                                                        />
                                                        {formatDate(view.date)}
                                                    </div>
                                                </td>
                                                <td style={{ verticalAlign: "middle" }}>
                                                    {view.unique_id || "-"}
                                                </td>
                                                <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                                    <button
                                                        className={`btn btn-xs ${view.isActive ? 'btn-success' : 'btn-secondary'}`}
                                                        title={view.isActive ? "Active" : "Inactive"}
                                                        onClick={() => handleToggleStatus(view.id, view.isActive)}
                                                    >
                                                        <FontAwesomeIcon icon={view.isActive ? faEye : faEyeSlash} />
                                                        <span className="ms-1">{view.isActive ? "Active" : "Inactive"}</span>
                                                    </button>
                                                </td>
                                                <td style={{ verticalAlign: "middle" }}>
                                                    <div className="d-flex gap-2">
                                                        {/* <button
                                                            className="btn btn-info btn-xs"
                                                            onClick={() => openDetailsModal(view)}
                                                            title="View Details"
                                                        >
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </button> */}
                                                        <PermissionWrapper module="analystView" action="edit">
                                                            <button
                                                                className="btn btn-warning btn-xs"
                                                                onClick={() => navigate(`/research/analystView/edit/${view.id}`)}
                                                                title="Edit Analyst View"
                                                            >
                                                                <FontAwesomeIcon icon={faPencil} />
                                                            </button>
                                                        </PermissionWrapper>
                                                        <PermissionWrapper module="analystView" action="delete">
                                                            <button
                                                                className="btn btn-danger btn-xs"
                                                                onClick={() => handleDelete(view.id)}
                                                                title="Delete Analyst View"
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </PermissionWrapper>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="text-center py-4">
                                                No Records Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        !loading && (
                            <div className="text-center py-5">
                                <h5>No Analyst Views Available</h5>
                                <p className="text-muted">Start by adding your first analyst view</p>
                                <PermissionWrapper module="analystView" action="add">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => navigate(`/research/analystView/add`)}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                        Add First Analyst View
                                    </button>
                                </PermissionWrapper>
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* View Details Modal */}
            {showDetailsModal && selectedView && (
                <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header d-flex align-items-center">
                                <h5 className="modal-title">Analyst View Details - {formatDate(selectedView.date)}</h5>
                                <button type="button" className="btn-close ms-auto" onClick={() => setShowDetailsModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <h6>Nifty Outlook</h6>
                                        <div className="p-2 bg-light rounded">
                                            {renderAllHighlights(selectedView.niftyOutlook?.highlights)}
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <h6>Bank Nifty Outlook</h6>
                                        <div className="p-2 bg-light rounded">
                                            {renderAllHighlights(selectedView.bankNiftyOutlook?.highlights)}
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <h6>Market Outlook Content</h6>
                                        <div className="p-3 border rounded" style={{ whiteSpace: "pre-wrap" }}>
                                            {selectedView.marketOutlook?.content || "No content available"}
                                        </div>
                                    </div>
                                    {selectedView.marketOutlook?.highlights && (
                                        <div className="col-md-12">
                                            <h6>Market Highlights</h6>
                                            <div className="p-2 bg-light rounded">
                                                {renderHighlights(selectedView.marketOutlook.highlights)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showDetailsModal && <div className="modal-backdrop fade show"></div>}

            <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
                <i className="ti-arrow-up"></i>
            </a>
        </div>
    );
};

export default AnalystViews;